import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
   BoltIcon, 
  MagnifyingGlassIcon, ChevronDownIcon, CpuChipIcon
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';
import Toast from '../../components/ui/Toast';

const InternshipTracker = () => {
  const [internships, setInternships] = useState([]);
  const [selectedInternshipId, setSelectedInternshipId] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); 
  const [submissions, setSubmissions] = useState([]);
  const [focusedUser, setFocusedUser] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // --- 1. LOAD INTERNSHIPS ONCE ---
  useEffect(() => {
    const fetchInternships = async () => {
      try {
        const res = await adminApi.getCompetitions();
        const data = res.competitions || res.data || [];
        setInternships(data.filter(item => 
          item.category?.toLowerCase() === 'internship' || 
          item.type?.toLowerCase() === 'internship'
        ));
      } catch (err) {
        console.error("Load Internship List Error:", err);
      }
    };
    fetchInternships();
  }, []);

  // --- 2. LOAD SUBMISSIONS (Triggers when selection changes) ---
const loadSubmissions = useCallback(async () => {
  if (!selectedInternshipId) {
      setSubmissions([]);
      return;
  }
  
  setLoading(true);
  setSubmissions([]); // <--- ADD THIS LINE: Clear old state before syncing new data
  
  try {
    const res = await adminApi.getPendingSubmissions(selectedInternshipId);
    const subData = res.data || res || [];
    setSubmissions(Array.isArray(subData) ? subData : []);
  } catch (err) {
    setNotification({ msg: "Sync Failed", type: 'error' });
  } finally { 
    setLoading(false); 
  }
}, [selectedInternshipId]);

  useEffect(() => { 
    loadSubmissions(); 
  }, [loadSubmissions]);

  // --- 3. CATEGORIZATION LOGIC ---
const categorizedUsers = useMemo(() => {
  // If no selection, return empty arrays immediately
  if (!selectedInternshipId) return { pending: [], ongoing: [], rejected: [], completed: [] };

  const groups = {};
  
  submissions.forEach(sub => {
    // CRITICAL FIX 1: Strict comparison to ensure reports from 
    // other internships don't "leak" into the current view.
    if (String(sub.competition_id) !== String(selectedInternshipId)) return;

    // Search filter
    if (searchTerm && !sub.user_name.toLowerCase().includes(searchTerm.toLowerCase())) return;

    // Create group for user if it doesn't exist
    if (!groups[sub.user_id]) {
      const totalWeeks = (sub.duration_months || 1) * 4;
      groups[sub.user_id] = {
        user_id: sub.user_id,
         internship_id: sub.internship_id,
        user_name: sub.user_name,
        internship_title: sub.internship_title,
        total_weeks: totalWeeks,
        reports: [],
        highest_approved_week: 0
      };
    }
    
    
    groups[sub.user_id].reports.push(sub);
    
    if (sub.status === 'approved' && sub.week_number > groups[sub.user_id].highest_approved_week) {
      groups[sub.user_id].highest_approved_week = sub.week_number;
    }
  });

  const list = Object.values(groups);
  
  // Sort reports (Latest week first)
  list.forEach(u => u.reports.sort((a, b) => b.week_number - a.week_number));

  return {
    pending: list.filter(u => u.reports.some(r => r.status === 'pending')),
    ongoing: list.filter(u => 
      u.highest_approved_week > 0 && 
      u.highest_approved_week < u.total_weeks && 
      !u.reports.some(r => r.status === 'pending')
    ),
    rejected: list.filter(u => 
      // User is "Rejected" ONLY if their most recent submission is rejected
      u.reports[0]?.status === 'rejected' && 
      !u.reports.some(r => r.status === 'pending')
    ),
    completed: list.filter(u => u.highest_approved_week >= u.total_weeks)
  };
}, [submissions, selectedInternshipId, searchTerm]);
  

  const handleReview = async (subId, status) => {
    setLoading(true);
    try {
      await adminApi.reviewSubmission({
        submissionId: subId,
        status,
        feedback: feedback || (status === 'approved' ? "Verified." : "Revision Required.")
      });

      setNotification({ msg: `Record ${status}`, type: 'success' });
      setFeedback('');
      
      // CRITICAL FIX: 
      // If we approved the last week, we reset the focusedUser so the UI 
      // doesn't try to show the "Old" data that was just archived.
      setFocusedUser(null); 

      // This re-fetches the CLEAN data from the database
      await loadSubmissions(); 
      
    } catch (err) {
      setNotification({ msg: "Action Failed", type: 'error' });
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-gray-200 font-mono p-6">
      {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}

      {/* --- HEADER --- */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6 rounded-3xl mb-8 shadow-2xl">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-600/20 rounded-2xl border border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
              <BoltIcon className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white uppercase tracking-tighter italic">Internship Control</h1>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.3em]">
                {loading ? 'SYNCING...' : selectedInternshipId ? 'MONITORING ACTIVE' : 'AWAITING SELECTION'}
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input 
                type="text" 
                placeholder="SEARCH PERSONNEL..." 
                className="bg-black border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:border-red-500 outline-none w-64 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="relative">
                <select 
                  className="bg-black border border-white/10 rounded-xl px-4 py-2 text-xs font-black text-white outline-none focus:border-red-500 transition-all appearance-none pr-10 cursor-pointer min-w-[200px]"
                  value={selectedInternshipId}
                  onChange={(e) => { 
                    setSelectedInternshipId(e.target.value); 
                    setFocusedUser(null); 
                    setActiveTab('pending');
                  }}
                >
                  <option value="">-- SELECT DEPLOYMENT --</option>
                  {internships.map(i => (
                      <option key={i.id} value={i.id}>{i.title.toUpperCase()}</option>
                  ))}
                </select>
                <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {!selectedInternshipId ? (
        <div className="flex flex-col items-center justify-center py-40 border-2 border-dashed border-white/5 rounded-[3rem] bg-white/[0.01]">
           <CpuChipIcon className="h-16 w-16 text-gray-800 mb-4 animate-pulse" />
           <p className="text-gray-600 font-black text-xs tracking-[0.6em] uppercase">Initialize System by Selecting a Program</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT COLUMN - USER LIST */}
          <div className="lg:col-span-4 space-y-6">
            <div className="grid grid-cols-2 gap-2 bg-black p-1 rounded-2xl border border-white/5">
              {['pending', 'ongoing', 'rejected', 'completed'].map(t => (
                <button
                  key={t}
                  onClick={() => { setActiveTab(t); setFocusedUser(null); }}
                  className={`py-2 text-[10px] font-black uppercase rounded-xl transition-all flex items-center justify-center gap-2 ${
                    activeTab === t ? 'bg-red-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'
                  }`}
                >
                  {t}
                  <span className="bg-white/10 px-1.5 py-0.5 rounded text-[8px]">{categorizedUsers[t]?.length || 0}</span>
                </button>
              ))}
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {categorizedUsers[activeTab].map(user => (
                <motion.div 
                  layout
                  key={user.user_id}
                  onClick={() => setFocusedUser(user)}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer group ${
                    focusedUser?.user_id === user.user_id ? 'bg-red-600/10 border-red-500' : 'bg-[#0a0a0a] border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-bold text-sm text-white uppercase">{user.user_name}</p>
                      <p className="text-[9px] text-gray-500 uppercase mt-1">Status: {activeTab}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-white italic">{user.highest_approved_week}/{user.total_weeks}</p>
                    </div>

                    {activeTab === 'completed' && (
  <button
    onClick={async (e) => {
      e.stopPropagation();

      const confirmDelete = window.confirm(
        `Delete completed internship record for ${user.user_name}?`
      );

      if (!confirmDelete) return;
      try {
        await adminApi.deleteCompletedIntern(
           user.internship_id, 
          user.user_id
        );
        setNotification({ msg: "Intern deleted", type: 'success' });
        await loadSubmissions();
      } catch (err) {
        setNotification({  msg: err.response?.data?.message || "Delete failed", 
           type: 'error' });
      }
    }}
    className="text-red-500 hover:text-red-300"
  >
    🗑
  </button>
)}
                  </div>
                  <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
                    <div className="bg-red-600 h-full transition-all duration-500" style={{ width: `${(user.highest_approved_week / user.total_weeks) * 100}%` }} />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN - REPORT DETAILS */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {focusedUser ? (
                <motion.div key={focusedUser.user_id} className="bg-[#0a0a0a] border border-white/10 rounded-[2.5rem] overflow-hidden">
                  <div className="p-8 bg-gradient-to-br from-red-600/10 to-transparent border-b border-white/5">
                    <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter">{focusedUser.user_name}</h2>
                  </div>
                  <div className="p-8 space-y-8 max-h-[65vh] overflow-y-auto custom-scrollbar">
                    {focusedUser.reports.map(report => (
                      <div key={report.id} className="relative pl-8 border-l border-white/10 space-y-4">
                        <div className={`absolute -left-1.5 top-0 h-3 w-3 rounded-full ${report.status === 'approved' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <h4 className="text-lg font-black text-white italic">WEEK {report.week_number}</h4>
                        <div className="bg-black/40 rounded-2xl p-6 border border-white/5 text-sm text-gray-300">
                          {report.report_content || "No Content"}
                        </div>
                        {report.status === 'pending' && (
                          <div className="bg-red-600/5 p-6 rounded-2xl border border-red-500/20 space-y-4">
                            <textarea 
                              className="w-full bg-black border border-white/10 rounded-xl p-4 text-xs text-white"
                              placeholder="Internal Remarks..."
                              value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                            />
                            <div className="flex gap-4">
                                <button onClick={() => handleReview(report.id, 'rejected')} className="flex-1 py-3 border border-red-500 text-red-500 uppercase text-[10px] font-bold rounded-xl">Reject</button>
                                <button onClick={() => handleReview(report.id, 'approved')} className="flex-1 py-3 bg-emerald-600 text-white uppercase text-[10px] font-bold rounded-xl">Approve</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <div className="h-[70vh] border-2 border-dashed border-white/5 rounded-[3rem] flex items-center justify-center">
                   <p className="uppercase font-black text-xs text-gray-800 tracking-widest">Select Personnel</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternshipTracker;